import { useRef, useState } from "react";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Badge, Box, Button, Flex, FormControl, FormLabel, Input, Text, Tooltip, useToast,
} from "@chakra-ui/react";
import { crm } from "../../../theme";
import {
  deleteHomologationInvoices, FiscalEnvironment, FiscalHomologationCleanup, FiscalIssuer, FiscalTokenSource, getHomologationCleanup,
  saveFiscalIssuer, saveFiscalIssuerToken,
} from "../../../services/fiscalService";

const ENV_LABEL: Record<FiscalEnvironment, string> = { homologacao: "homologação", producao: "produção" };

const tokenBadge = (source?: FiscalTokenSource | null) => {
  if (source === "server") return <Tooltip hasArrow label="Definido em variável de ambiente no servidor (vale por cima do salvo aqui)."><Badge colorScheme="blue" cursor="help">Servidor</Badge></Tooltip>;
  if (source === "saved") return <Badge colorScheme="green">Salvo ✓</Badge>;
  if (source === "unreadable") return <Tooltip hasArrow label="Salvo em outro servidor: não abre aqui. Salve o token de novo."><Badge colorScheme="orange" cursor="help">Ilegível</Badge></Tooltip>;
  return <Badge>Não cadastrado</Badge>;
};

type Confirm = null | { kind: "producao" | "homologacao" } | { kind: "cleanup"; summary: FiscalHomologationCleanup };

/**
 * Admin do emitente salvo: tokens da Focus (só escrita, testados na Focus), troca homologação ↔ produção e
 * limpeza das notas de teste. Trabalha sobre o emitente SALVO (alteração pendente no formulário bloqueia a troca de ambiente).
 */
const FiscalIssuerAdmin = ({ issuer, dirty, onSaved }: { issuer: FiscalIssuer; dirty: boolean; onSaved: (issuer: FiscalIssuer) => void }) => {
  const toast = useToast();
  const [tokens, setTokens] = useState<Record<FiscalEnvironment, string>>({ homologacao: "", producao: "" });
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [typed, setTyped] = useState("");
  const cancelRef = useRef<HTMLButtonElement>(null);
  const production = issuer.environment === "producao";

  const fail = (title: string, e: any) => toast({ status: "error", title, description: e?.message, duration: 9000, isClosable: true });

  const saveToken = async (env: FiscalEnvironment, remove = false) => {
    setBusy(`token-${env}`);
    try {
      const result = await saveFiscalIssuerToken(issuer.id!, env, remove ? null : tokens[env]);
      setTokens((t) => ({ ...t, [env]: "" }));
      onSaved(result);
      toast({ status: "success", title: remove ? `Token de ${ENV_LABEL[env]} removido` : `Token de ${ENV_LABEL[env]} conferido na Focus e salvo` });
    } catch (e: any) {
      fail("Token não salvo", e);
    } finally {
      setBusy(null);
    }
  };

  const openCleanup = async () => {
    setBusy("cleanup");
    try {
      setTyped("");
      setConfirm({ kind: "cleanup", summary: await getHomologationCleanup(issuer.id!) });
    } catch (e: any) {
      fail("Não foi possível contar as notas de teste", e);
    } finally {
      setBusy(null);
    }
  };

  const run = async () => {
    if (!confirm) return;
    setBusy("confirm");
    try {
      if (confirm.kind === "cleanup") {
        const message = await deleteHomologationInvoices(issuer.id!);
        toast({ status: "success", title: message || "Notas de teste apagadas" });
      } else {
        // Padrões nulos = servidor mantém os gravados (não regrava padrão de remessa em quem não é conta e ordem)
        const result = await saveFiscalIssuer({ ...issuer, environment: confirm.kind, entryDefaults: null, shipmentDefaults: null, saleDefaults: null });
        onSaved(result);
        toast({ status: "success", title: `${result.name} agora emite em ${ENV_LABEL[result.environment]}` });
      }
      setConfirm(null);
    } catch (e: any) {
      fail(confirm.kind === "cleanup" ? "Notas não apagadas" : "Ambiente não alterado", e);
    } finally {
      setBusy(null);
    }
  };

  const cleanupWord = "APAGAR";
  const canConfirm = !confirm ? false
    : confirm.kind === "producao" ? typed.trim().toUpperCase() === issuer.name.trim().toUpperCase()
    : confirm.kind === "cleanup" ? confirm.summary.count > 0 && confirm.summary.blockers.length === 0 && typed.trim().toUpperCase() === cleanupWord
    : true;

  return (
    <Box mt="14px" p="12px" border={`1px dashed ${crm.border}`} borderRadius="10px">
      <Flex justify="space-between" align="center" wrap="wrap" gap="8px">
        <Box>
          <Text fontSize="13px" fontWeight="700">Ambiente de emissão</Text>
          <Text fontSize="12px" color={crm.textMuted}>
            {production ? "Produção: notas com valor fiscal." : "Homologação: notas de teste, sem valor fiscal."}
          </Text>
        </Box>
        <Tooltip hasArrow isDisabled={!dirty} label="Salve as alterações do emitente antes de trocar o ambiente.">
          <Button size="sm" variant={production ? "outline" : "solid"} colorScheme={production ? "purple" : "red"} isDisabled={dirty}
            onClick={() => { setTyped(""); setConfirm({ kind: production ? "homologacao" : "producao" }); }}>
            {production ? "Voltar para homologação" : "Mudar para produção"}
          </Button>
        </Tooltip>
      </Flex>

      <Text fontSize="13px" fontWeight="700" mt="14px">Tokens da Focus NFe</Text>
      <Text fontSize="12px" color={crm.textMuted} mb="8px">
        Painel da Focus › Minhas empresas › Editar › Tokens. O token é testado na Focus antes de salvar e fica criptografado; nunca é exibido de volta.
      </Text>
      {(["homologacao", "producao"] as FiscalEnvironment[]).map((env) => {
        const source = env === "producao" ? issuer.productionTokenSource : issuer.homologationTokenSource;
        return (
          <Flex key={env} align="flex-end" gap="8px" wrap="wrap" mt="6px">
            <FormControl maxW="420px">
              <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">
                <Flex as="span" gap="6px" align="center">Token de {ENV_LABEL[env]} {tokenBadge(source)}</Flex>
              </FormLabel>
              <Input size="sm" type="password" autoComplete="new-password" maxLength={200}
                placeholder={source ? "Colar novo token para trocar" : "Colar token"}
                value={tokens[env]} onChange={(e) => setTokens((t) => ({ ...t, [env]: e.target.value }))} />
            </FormControl>
            <Button size="sm" isDisabled={!tokens[env].trim()} isLoading={busy === `token-${env}`} onClick={() => saveToken(env)}>
              Salvar token
            </Button>
            {source === "saved" || source === "unreadable" ? (
              <Button size="sm" variant="ghost" color={crm.textMuted} isDisabled={busy !== null} onClick={() => saveToken(env, true)}>
                Remover
              </Button>
            ) : null}
          </Flex>
        );
      })}

      <Flex justify="space-between" align="center" wrap="wrap" gap="8px" mt="16px">
        <Box>
          <Text fontSize="13px" fontWeight="700">Notas de teste</Text>
          <Text fontSize="12px" color={crm.textMuted}>
            Apaga as notas enviadas em homologação e as geradas a partir delas, liberando os processos para a nota real.
          </Text>
        </Box>
        <Button size="sm" variant="outline" colorScheme="red" isLoading={busy === "cleanup"} onClick={openCleanup}>
          Apagar notas de teste
        </Button>
      </Flex>

      <AlertDialog isOpen={confirm !== null} leastDestructiveRef={cancelRef} onClose={() => busy !== "confirm" && setConfirm(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="16px">
              {confirm?.kind === "producao" ? "Mudar para produção?" : confirm?.kind === "homologacao" ? "Voltar para homologação?" : "Apagar notas de teste?"}
            </AlertDialogHeader>
            <AlertDialogBody fontSize="14px">
              {confirm?.kind === "producao" && (
                <>
                  <Text>As próximas notas de <b>{issuer.name}</b> vão para a SEFAZ <b>com valor fiscal</b>. Antes, confira no painel da Focus o próximo número de produção (depois do último emitido pelo ERP).</Text>
                  <Text mt="10px" fontSize="13px">Digite o nome do emitente para confirmar:</Text>
                  <Input size="sm" mt="6px" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={issuer.name} />
                </>
              )}
              {confirm?.kind === "homologacao" && (
                <Text>As próximas notas de <b>{issuer.name}</b> saem em homologação (sem valor fiscal). Notas já autorizadas em produção continuam valendo.</Text>
              )}
              {confirm?.kind === "cleanup" && (confirm.summary.count === 0 ? (
                <Text>Nenhuma nota de teste deste emitente.</Text>
              ) : (
                <>
                  <Text>
                    <b>{confirm.summary.count}</b> nota(s) saem do sistema ({confirm.summary.authorized} autorizada(s) em homologação), incluindo remessas, filhotes, vendas e rascunhos gerados a partir delas. Na SEFAZ de homologação nada muda.
                  </Text>
                  {confirm.summary.blockers.length > 0 ? (
                    <Text mt="10px" color="red.500">
                      Bloqueado: há notas de produção geradas a partir de notas de teste ({confirm.summary.blockers.join(", ")}).
                    </Text>
                  ) : (
                    <>
                      <Text mt="10px" fontSize="13px">Digite {cleanupWord} para confirmar:</Text>
                      <Input size="sm" mt="6px" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={cleanupWord} />
                    </>
                  )}
                </>
              ))}
            </AlertDialogBody>
            <AlertDialogFooter gap="8px">
              <Button ref={cancelRef} size="sm" variant="ghost" isDisabled={busy === "confirm"} onClick={() => setConfirm(null)}>Cancelar</Button>
              {!(confirm?.kind === "cleanup" && confirm.summary.count === 0) && (
                <Button size="sm" colorScheme={confirm?.kind === "homologacao" ? "purple" : "red"} isDisabled={!canConfirm}
                  isLoading={busy === "confirm"} onClick={run}>
                  {confirm?.kind === "cleanup" ? "Apagar" : "Confirmar"}
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default FiscalIssuerAdmin;
